import {
  Box,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerOverlay,
  Flex,
  Tabs,
  Text,
  useBreakpointValue,
  useDisclosure,
} from '@chakra-ui/react'
import { Check, ChevronsUpDown } from 'lucide-react'
import { ReactElement, useEffect, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import StyledTab from './StyledTab'
import StyledTabList from './StyledTabList'
import TabSwipePager from './TabSwipePager'
import { useHideTabBar } from '../../../hooks/useHideTabBar'
import { useEdgeSwipeOpen } from '../../../hooks/useEdgeSwipeOpen'
import { useDisableNativeSwipeBack } from '../../../hooks/useDisableNativeSwipeBack'
import { useUIStore } from '../../../stores/uiStore'
import { useTabBarStore } from '../../../stores/tabBarStore'
import { isFullHeightPath } from '../../../utils/constants/fullHeightPages'

interface TabRoute {
  label: string | ReactElement
  path: string
  hasExclamation?: boolean
  matchSubroutes?: boolean
}

const TabWrapper = ({ children, tabRoutes }: { children: ReactElement; tabRoutes: TabRoute[] }) => {
  const location = useLocation()
  const isFullHeightPage = isFullHeightPath(location.pathname)
  const navigate = useNavigate()
  const [index, setIndex] = useState(0)
  const { isOpen, onOpen, onClose } = useDisclosure()
  const isMobile = useBreakpointValue({ base: true, md: false }, { ssr: false }) ?? false
  const setMobileNavOpen = useUIStore((s) => s.setMobileNavOpen)
  // When anything has hidden the bottom tab bar (e.g. ViewItemDrawer, filters
  // drawer, mobile nav drawer), it owns the screen and we must NOT process
  // tab-swipe gestures behind it. Otherwise a swipe inside an overlaid drawer
  // bleeds through to the pager and navigates the underlying route.
  const isTabBarHidden = useTabBarStore((s) => s.isHidden)
  // Whether the current location matches one of this TabWrapper's tab routes.
  // When a subroute like /add-manual is rendered THROUGH TabWrapper but isn't
  // part of the tab list, the pager doesn't show and native iOS back-swipe
  // should own the gesture (so the user can swipe back to the Items list).
  const isOnTabRoute = tabRoutes.some((tabRoute) =>
    tabRoute.matchSubroutes
      ? location.pathname.startsWith(tabRoute.path)
      : location.pathname === tabRoute.path,
  )
  const swipeEnabled = isMobile && !isTabBarHidden && isOnTabRoute

  // Tab-root pages own horizontal swipes (TabSwipePager between tabs,
  // edge-swipe-open-sidebar). Suppress the iOS native back-swipe ONLY while
  // the location matches a tab route — subroutes (e.g. /add-manual) rendered
  // through this wrapper should keep native back-swipe for true page-pop feel.
  useDisableNativeSwipeBack(isMobile && isOnTabRoute, 'tab-root')

  // On the first tab, an edge-swipe from the left should open the side menu.
  // We register a window-level listener (independent of the in-content pager)
  // with a wider zone since iOS reserves the extreme left strip for system
  // gestures and may not deliver touchstart events that begin in the first
  // few pixels reliably.
  useEdgeSwipeOpen(() => setMobileNavOpen(true), {
    enabled: swipeEnabled && index === 0,
    edgeSize: 40,
    minDistance: 50,
  })

  // Hide the mobile section switcher on detail pages (keeps desktop tabs visible)
  const isSeizureDetailRoute = /^\/react\/custom-entities\/seizures\/[^/]+\/?$/.test(location.pathname)
  const isSellerDetailRoute = /^\/react\/sellers\/(?!list|add|edit)([^/]+)\/?$/.test(location.pathname)
  const hideMobileSwitcher = isSeizureDetailRoute || isSellerDetailRoute

  useHideTabBar(isOpen, 'tab-wrapper-picker')

  useEffect(() => {
    // Find the best matching tab: prefer exact matches, then the longest
    // prefix match among matchSubroutes tabs (so /octopus/o2o beats /octopus).
    let newIndex = tabRoutes.findIndex((r) => location.pathname === r.path)
    if (newIndex === -1) {
      let bestLen = 0
      tabRoutes.forEach((r, i) => {
        if (r.matchSubroutes && location.pathname.startsWith(r.path) && r.path.length > bestLen) {
          bestLen = r.path.length
          newIndex = i
        }
      })
    }
    setIndex(newIndex)
  }, [location, tabRoutes])

  if (index === -1) {
    return children
  }

  const activeRoute = tabRoutes[index]
  const hasPendingElsewhere = tabRoutes.some((r, i) => i !== index && r.hasExclamation)

  return (
    <Box
      w="100%"
      maxW="100%"
      minW={0}
      overflowX="clip"
      // Pass the flex height from ReactWrapper down to the tab content, so a
      // full-height page (e.g. the Octopus graph canvas) fills the viewport
      // rather than subtracting the header + tab strip by hand. Tab pages that
      // scroll the shell instead must stay unconstrained.
      h={isFullHeightPage ? { md: '100%' } : undefined}
      display={isFullHeightPage ? { md: 'flex' } : undefined}
      flexDirection={isFullHeightPage ? { md: 'column' } : undefined}
    >
      {/* Mobile: compact section switcher chip + bottom sheet picker */}
      <Box display={hideMobileSwitcher ? 'none' : { base: 'block', md: 'none' }} pt="12px" pb="8px" px="4px">
        <Flex align="center" justify="space-between" gap="10px">
          <Flex align="center" gap="8px" minW={0} flex={1}>
            <Text
              fontSize="10px"
              fontWeight="700"
              color="neutral.400"
              textTransform="uppercase"
              letterSpacing="0.1em"
              flexShrink={0}
            >
              {index + 1}/{tabRoutes.length}
            </Text>
            <Text
              fontSize="22px"
              fontWeight="700"
              color="neutral.900"
              letterSpacing="-0.02em"
              lineHeight="1.1"
              noOfLines={1}
              flex={1}
              minW={0}
            >
              {activeRoute?.label}
            </Text>
          </Flex>
          <Box
            as="button"
            onClick={onOpen}
            aria-label="Switch section"
            flexShrink={0}
            h="36px"
            px="12px"
            borderRadius="full"
            bg="white"
            display="inline-flex"
            alignItems="center"
            gap="6px"
            boxShadow="0 2px 8px 0 rgba(79, 70, 229, 0.18)"
            _active={{ transform: 'scale(0.96)' }}
            transition="transform 0.12s ease-out"
            sx={{ touchAction: 'manipulation' }}
            position="relative"
          >
            <Text fontSize="12px" fontWeight="600" color="secondary.500" letterSpacing="-0.01em">
              Switch
            </Text>
            <Box as={ChevronsUpDown} color="secondary.500" w="14px" h="14px" strokeWidth={2.5} />
            {hasPendingElsewhere && (
              <Box
                position="absolute"
                top="-3px"
                right="-3px"
                w="10px"
                h="10px"
                borderRadius="full"
                bg="red.500"
                boxShadow="0 0 0 2px white"
              />
            )}
          </Box>
        </Flex>

        {/* Progress rail */}
        <Flex mt="10px" gap="3px" w="100%">
          {tabRoutes.map((_, i) => (
            <Box
              key={i}
              flex={1}
              h="3px"
              borderRadius="full"
              bg={i === index ? 'secondary.500' : 'neutral.100'}
              transition="background-color 0.25s ease-out"
              boxShadow={i === index ? '0 0 0 1px rgba(79,70,229,0.12)' : 'none'}
            />
          ))}
        </Flex>
      </Box>

      {/* Mobile: bottom sheet picker */}
      {isOpen && (
      <Drawer isOpen={isOpen} placement="bottom" onClose={onClose}>
        <DrawerOverlay bg="rgba(10, 10, 20, 0.55)" />
        <DrawerContent
          borderTopRadius="22px"
          maxH="82vh"
          boxShadow="0 -12px 40px rgba(0,0,0,0.22)"
        >
          <Flex justify="center" pt="10px" pb="2px">
            <Box w="40px" h="4px" borderRadius="full" bg="neutral.150" />
          </Flex>
          <Flex align="baseline" justify="space-between" px="20px" pt="8px" pb="6px">
            <Text
              fontSize="11px"
              fontWeight="700"
              color="neutral.400"
              textTransform="uppercase"
              letterSpacing="0.1em"
            >
              Jump to section
            </Text>
            <Text fontSize="11px" fontWeight="500" color="neutral.400">
              {tabRoutes.length} total
            </Text>
          </Flex>
          <DrawerBody
            pt="4px"
            pb="calc(env(safe-area-inset-bottom) + 14px)"
            px="12px"
            overflowY="auto"
          >
            <Box as="ul" listStyleType="none" m={0} p={0}>
              {tabRoutes.map((tab, i) => {
                const isActive = i === index
                return (
                  <Box
                    as="li"
                    key={i}
                    listStyleType="none"
                    sx={{
                      '& + &': {
                        mt: '2px',
                      },
                    }}
                  >
                    <Flex
                      as="button"
                      onClick={() => {
                        navigate(tab.path)
                        onClose()
                      }}
                      w="100%"
                      minH="56px"
                      px="14px"
                      py="10px"
                      borderRadius="14px"
                      alignItems="center"
                      gap="12px"
                      bg={isActive ? 'secondary.50' : 'white'}
                      borderWidth="1px"
                      borderColor={isActive ? 'secondary.200' : 'transparent'}
                      transition="background-color 0.15s ease-out, border-color 0.15s ease-out"
                      _active={{ bg: isActive ? 'secondary.50' : 'neutral.50' }}
                      sx={{ touchAction: 'manipulation' }}
                      textAlign="left"
                      role="menuitemradio"
                      aria-checked={isActive}
                    >
                      {/* Index bubble */}
                      <Flex
                        w="32px"
                        h="32px"
                        borderRadius="10px"
                        align="center"
                        justify="center"
                        flexShrink={0}
                        bg={isActive ? 'secondary.500' : 'neutral.50'}
                        color={isActive ? 'white' : 'neutral.500'}
                        boxShadow={isActive ? '0 2px 8px 0 rgba(79,70,229,0.3)' : 'none'}
                        transition="background-color 0.2s, box-shadow 0.2s"
                      >
                        <Text
                          fontSize="12px"
                          fontWeight="700"
                          sx={{ fontVariantNumeric: 'tabular-nums' }}
                        >
                          {String(i + 1).padStart(2, '0')}
                        </Text>
                      </Flex>

                      {/* Label */}
                      <Flex direction="column" flex={1} minW={0}>
                        <Text
                          fontSize="15px"
                          fontWeight={isActive ? '700' : '600'}
                          color={isActive ? 'secondary.700' : 'neutral.900'}
                          letterSpacing="-0.01em"
                          lineHeight="1.25"
                          noOfLines={1}
                        >
                          {tab.label}
                        </Text>
                        {isActive ? (
                          <Text fontSize="11px" fontWeight="600" color="secondary.600" mt="2px" letterSpacing="0.02em">
                            Current section
                          </Text>
                        ) : (
                          <Text fontSize="11px" fontWeight="500" color="neutral.400" mt="2px">
                            Tap to switch
                          </Text>
                        )}
                      </Flex>

                      {/* Trailing indicator */}
                      {isActive ? (
                        <Flex
                          w="24px"
                          h="24px"
                          borderRadius="full"
                          bg="secondary.500"
                          align="center"
                          justify="center"
                          flexShrink={0}
                        >
                          <Box as={Check} color="white" w="14px" h="14px" strokeWidth={3} />
                        </Flex>
                      ) : tab.hasExclamation ? (
                        <Flex
                          align="center"
                          gap="6px"
                          flexShrink={0}
                          bg="red.50"
                          borderWidth="1px"
                          borderColor="red.100"
                          borderRadius="full"
                          px="8px"
                          h="22px"
                        >
                          <Box w="6px" h="6px" borderRadius="full" bg="red.500" />
                          <Text fontSize="10px" fontWeight="700" color="red.600" letterSpacing="0.02em">
                            NEW
                          </Text>
                        </Flex>
                      ) : null}
                    </Flex>
                  </Box>
                )
              })}
            </Box>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
      )}

      {/* Desktop: original tab strip */}
      <Box display={{ base: 'none', md: 'block' }} flexShrink={0}>
        <Tabs p="20px 0" index={index} onChange={(i) => setIndex(i)}>
          <StyledTabList>
            {tabRoutes.map((tabRoute, i) => (
              <Link to={tabRoute.path} key={i}>
                <StyledTab key={i} hasExclamation={tabRoute.hasExclamation}>
                  {tabRoute.label}
                </StyledTab>
              </Link>
            ))}
          </StyledTabList>
        </Tabs>
      </Box>
      <TabSwipePager
        enabled={swipeEnabled && !hideMobileSwitcher}
        fullHeight={isFullHeightPage}
        index={index}
        count={tabRoutes.length}
        onChangeIndex={(i) => navigate(tabRoutes[i].path)}
        onSwipeRightOnFirst={() => setMobileNavOpen(true)}
      >
        {children}
      </TabSwipePager>
    </Box>
  )
}

export default TabWrapper
